/**
 * Bulk Order Form — Web Component (v2)
 *
 * Product page version:
 * - Variants come from product (data-variant-id on <option>)
 * - No discount tiers
 * - Multi-step confirmation (desktop) / modal (mobile)
 * - Direct checkout redirect
 *
 * Upload flow unchanged: Cloudflare R2 parallel upload → Worker /submit
 */
class BulkOrderForm extends HTMLElement {
    constructor() {
        super();
        this.files = [];
        this.maxFileSizeMB = parseInt(this.dataset.maxFileSize || '10', 10);
        this.isUploading = false;
        this.CONCURRENCY = 5;
        this.MOBILE_BREAKPOINT = 768;
        this.minQty = parseInt(this.dataset.minQty || '100', 10);
    }

    connectedCallback() {
        this.form = this.querySelector('form');
        this.dropzone = this.querySelector('.bulk-order-form__dropzone');
        this.fileInput = this.querySelector('.bulk-order-form__file-input');
        this.fileListEl = this.querySelector('.bulk-order-form__file-list');
        this.quantityInput = this.querySelector('#BulkOrder-quantity');
        this.progressEl = this.querySelector('.bulk-order-form__progress');
        this.progressFill = this.querySelector('.bulk-order-form__progress-fill');
        this.progressText = this.querySelector('.bulk-order-form__progress-text');
        this.submitBtn = this.querySelector('.bulk-order-form__submit-btn');
        this.sizeSelect = this.querySelector('#BulkOrder-size');
        this.totalEl = this.querySelector('.bulk-order-form__total');
        this.totalValueEl = this.querySelector('#BulkOrder-total');
        this.hiddenUnitPrice = this.querySelector('#BulkOrder-unit-price');
        this.hiddenTotalPrice = this.querySelector('#BulkOrder-total-price');
        this.successMsg = this.querySelector('.bulk-order-form__success');
        this.errorMsg = this.querySelector('.bulk-order-form__error');
        this.uploadHint = this.querySelector('.bulk-order-form__upload-hint');

        // Step elements
        this.stepIndicators = this.querySelectorAll('.bulk-order-form__step-indicator');
        this.stepContents = this.querySelectorAll('.bulk-order-form__step');
        this.summaryContent = this.querySelector('#BulkOrder-summary-content');
        this.editBtn = this.querySelector('#BulkOrder-edit-btn');
        this.checkoutBtn = this.querySelector('#BulkOrder-checkout-btn');

        // Modal elements
        this.modal = this.querySelector('#BulkOrder-modal');
        this.modalBackdrop = this.querySelector('.bulk-order-form__modal-backdrop');
        this.modalClose = this.querySelector('#BulkOrder-modal-close');
        this.modalSummaryContent = this.querySelector('#BulkOrder-modal-summary-content');
        this.modalCheckoutBtn = this.querySelector('#BulkOrder-modal-checkout-btn');

        this.bindEvents();
        this.updateUploadHint();
        this.updateTotal();

        // Set min deadline = today + 3 days
        const deadlineInput = this.querySelector('#BulkOrder-deadline');
        if (deadlineInput) {
            const minDate = new Date();
            minDate.setDate(minDate.getDate() + 3);
            deadlineInput.setAttribute('min', minDate.toISOString().split('T')[0]);
        }
    }

    // ── Responsive check ──
    isMobile() {
        return window.innerWidth < this.MOBILE_BREAKPOINT;
    }

    bindEvents() {
        this.dropzone?.addEventListener('click', () => this.fileInput?.click());
        this.dropzone?.addEventListener('dragover', (e) => { e.preventDefault(); this.dropzone.classList.add('drag-over'); });
        this.dropzone?.addEventListener('dragleave', (e) => { e.preventDefault(); this.dropzone.classList.remove('drag-over'); });
        this.dropzone?.addEventListener('drop', (e) => this.handleDrop(e));
        this.fileInput?.addEventListener('change', (e) => this.handleFileSelect(e));
        this.quantityInput?.addEventListener('input', () => { this.handleQuantityChange(); this.updateTotal(); });
        this.sizeSelect?.addEventListener('change', () => this.updateTotal());

        // Intercept form submissions
        if (this.form) {
            this.form.addEventListener('submit', (e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.handleSubmit();
            }, true);
        }

        if (this.submitBtn) {
            this.submitBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.handleSubmit();
            });
        }

        // Step 2 buttons
        this.editBtn?.addEventListener('click', () => this.goToStep(1));
        this.checkoutBtn?.addEventListener('click', () => this.redirectToCheckout());

        // Modal buttons
        this.modalBackdrop?.addEventListener('click', () => this.closeModal());
        this.modalClose?.addEventListener('click', () => this.closeModal());
        this.modalCheckoutBtn?.addEventListener('click', () => this.redirectToCheckout());
    }

    // ── Step navigation ──
    goToStep(stepNum) {
        this.stepIndicators.forEach(ind => {
            const s = parseInt(ind.dataset.step, 10);
            ind.classList.toggle('active', s <= stepNum);
            ind.classList.toggle('completed', s < stepNum);
        });
        this.stepContents.forEach(content => {
            content.classList.toggle('active', parseInt(content.dataset.step, 10) === stepNum);
        });
        window.scrollTo({ top: this.offsetTop - 20, behavior: 'smooth' });
    }

    // ── Modal ──
    openModal() {
        if (this.modal) {
            this.modal.classList.add('active');
            document.body.style.overflow = 'hidden';
        }
    }

    closeModal() {
        if (this.modal) {
            this.modal.classList.remove('active');
            document.body.style.overflow = '';
        }
    }

    // ── Quantity ──
    getMaxFiles() {
        return Math.max(0, parseInt(this.quantityInput?.value || '0', 10));
    }

    handleQuantityChange() {
        const raw = parseInt(this.quantityInput?.value || '0', 10);
        const qtyError = this.querySelector('#BulkOrder-qty-error');
        if (raw < this.minQty && raw > 0) {
            if (qtyError) qtyError.style.display = 'block';
        } else {
            if (qtyError) qtyError.style.display = 'none';
        }
        const maxFiles = this.getMaxFiles();
        if (this.files.length > maxFiles && maxFiles > 0) {
            this.files = this.files.slice(0, maxFiles);
            this.renderFileList();
        }
        this.updateUploadHint();
    }

    updateUploadHint() {
        if (!this.uploadHint) return;
        const maxFiles = this.getMaxFiles();
        this.uploadHint.textContent = maxFiles > 0
            ? `Max. 10MB per file · ${this.files.length}/${maxFiles} files`
            : 'Max. 10MB per file · Enter quantity first';
    }

    // ── Total (no discounts) ──
    updateTotal() {
        const selectedOption = this.sizeSelect?.selectedOptions?.[0];
        const basePrice = parseFloat(selectedOption?.dataset?.price || '0');
        const qty = parseInt(this.quantityInput?.value || '0', 10);
        const total = basePrice * qty;

        if (basePrice > 0 && qty > 0) {
            if (this.totalEl) this.totalEl.style.display = '';
            if (this.totalValueEl) this.totalValueEl.innerHTML = `<span class="bulk-order-form__discounted-price">$${total.toFixed(2)}</span>`;
            if (this.hiddenUnitPrice) this.hiddenUnitPrice.value = `$${basePrice.toFixed(2)}`;
            if (this.hiddenTotalPrice) this.hiddenTotalPrice.value = `$${total.toFixed(2)}`;
        } else {
            if (this.totalEl) this.totalEl.style.display = 'none';
        }
    }

    // ── File handling ──
    handleDrop(e) {
        e.preventDefault();
        this.dropzone?.classList.remove('drag-over');
        this.processFiles(Array.from(e.dataTransfer?.files || []));
    }

    handleFileSelect(e) {
        this.processFiles(Array.from(e.target?.files || []));
        if (this.fileInput) this.fileInput.value = '';
    }

    processFiles(incomingFiles) {
        const maxFiles = this.getMaxFiles();
        if (maxFiles <= 0) { alert('Please enter quantity first.'); return; }
        const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff'];
        let added = 0;
        for (const file of incomingFiles) {
            if (this.files.length >= maxFiles) { if (added === 0) alert(`Maximum ${maxFiles} files allowed.`); break; }
            if (!allowed.includes(file.type)) continue;
            const sizeMB = file.size / (1024 * 1024);
            if (sizeMB > this.maxFileSizeMB) { alert(`"${file.name}" exceeds ${this.maxFileSizeMB}MB`); continue; }
            this.files.push({ file, thumbUrl: URL.createObjectURL(file), id: 'f_' + Math.random().toString(36).substr(2, 8), sizeMB: sizeMB.toFixed(1) });
            added++;
        }
        this.renderFileList();
        this.updateUploadHint();
    }

    renderFileList() {
        if (!this.fileListEl) return;
        if (!this.files.length) { this.fileListEl.innerHTML = ''; return; }
        this.fileListEl.innerHTML = this.files.map(f => `
      <div class="bulk-order-form__file-item" data-id="${f.id}">
        <img class="bulk-order-form__file-thumb" src="${f.thumbUrl}" alt="" />
        <span class="bulk-order-form__file-name" title="${this.esc(f.file.name)}">${this.esc(this.trunc(f.file.name))}</span>
        <span class="bulk-order-form__file-size">${f.sizeMB}MB</span>
        <button type="button" class="bulk-order-form__file-delete" data-id="${f.id}" title="Remove">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
        </button>
      </div>`).join('');
        this.fileListEl.querySelectorAll('.bulk-order-form__file-delete').forEach(btn => {
            btn.addEventListener('click', () => this.removeFile(btn.dataset.id));
        });
    }

    removeFile(id) {
        const f = this.files.find(x => x.id === id);
        if (f) URL.revokeObjectURL(f.thumbUrl);
        this.files = this.files.filter(x => x.id !== id);
        this.renderFileList();
        this.updateUploadHint();
    }

    trunc(name) { return name.length <= 20 ? name : name.substring(0, 8) + '….' + name.split('.').pop(); }
    esc(str) { const d = document.createElement('div'); d.textContent = str; return d.innerHTML; }

    // ── Upload config ──
    getUploadConfig() {
        return {
            url: (this.dataset.uploadUrl || '').trim().replace(/\/+$/, ''),
            apiKey: (this.dataset.uploadKey || '').trim(),
        };
    }
    isUploadConfigured() { const c = this.getUploadConfig(); return !!(c.url && c.apiKey); }

    // ── Upload single file ──
    async uploadSingleFile(file, retries = 2) {
        const cfg = this.getUploadConfig();
        const fd = new FormData();
        fd.append('file', file);
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                const res = await fetch(`${cfg.url}/upload`, { method: 'POST', headers: { 'X-API-Key': cfg.apiKey }, body: fd });
                const result = await res.json();
                if (!res.ok || result.error) throw new Error(result.error || `HTTP ${res.status}`);
                return result.url;
            } catch (err) {
                if (attempt < retries) { await new Promise(r => setTimeout(r, 1000 * (attempt + 1))); continue; }
                throw err;
            }
        }
    }

    // ── Parallel upload ──
    async uploadAllFiles() {
        const total = this.files.length;
        const urls = [];
        let done = 0;
        for (let i = 0; i < total; i += this.CONCURRENCY) {
            const chunk = this.files.slice(i, Math.min(i + this.CONCURRENCY, total));
            const results = await Promise.all(chunk.map(async entry => {
                const url = await this.uploadSingleFile(entry.file);
                done++;
                this.setProgress(Math.round((done / total) * 100), `Uploading ${done}/${total}...`);
                return url;
            }));
            urls.push(...results);
        }
        this.setProgress(100, 'All photos uploaded!');
        return urls;
    }

    setProgress(pct, text) {
        if (this.progressEl) this.progressEl.classList.add('active');
        if (this.progressFill) this.progressFill.style.width = pct + '%';
        if (this.progressText) this.progressText.textContent = text;
    }
    hideProgress() { if (this.progressEl) this.progressEl.classList.remove('active'); }

    // ── Collect form data ──
    getFormData() {
        return {
            firstName: this.querySelector('#BulkOrder-first-name')?.value || '',
            lastName: this.querySelector('#BulkOrder-last-name')?.value || '',
            email: this.querySelector('#BulkOrder-email')?.value || '',
            quantity: this.quantityInput?.value || '',
            size: this.sizeSelect?.selectedOptions?.[0]?.textContent?.trim() || '',
            sizeRaw: this.sizeSelect?.value || '',
            variantId: this.sizeSelect?.selectedOptions?.[0]?.dataset?.variantId || '',
            unitPrice: this.hiddenUnitPrice?.value || '',
            totalPrice: this.hiddenTotalPrice?.value || '',
            deadline: this.querySelector('#BulkOrder-deadline')?.value || '',
            notes: this.querySelector('#BulkOrder-notes')?.value || '',
        };
    }

    // ── Redirect to checkout ──
    async redirectToCheckout() {
        const formData = this.getFormData();
        if (!formData.variantId) return;
        const qty = parseInt(formData.quantity, 10) || 1;

        try {
            // Add to cart with line item property showing size + quantity
            const properties = {};
            if (formData.sizeRaw) {
                properties['Photo Size'] = `${formData.sizeRaw} - ${qty} items`;
            }
            // Attach gallery link to the order
            if (this.galleryUrl) {
                properties['📸 Photos'] = this.galleryUrl;
            }
            const noteUrl = this.zipUrl || this.galleryUrl;
            if (noteUrl) {
                properties._bulk_zip_url = noteUrl;
                properties._bulk_upload_id = `bulk-${Date.now()}`;
            }

            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    id: parseInt(formData.variantId),
                    quantity: qty,
                    properties
                })
            });

            // Preserve customer text and photo ZIP entries owned by other cart lines.
            if (noteUrl) {
                if (!window.DearestPhotoOrderNote) throw new Error('Order note synchronization is unavailable.');
                await window.DearestPhotoOrderNote.sync();
            }

            // Build checkout URL with pre-filled customer info
            const params = new URLSearchParams();
            if (formData.email) params.set('checkout[email]', formData.email);
            if (formData.firstName) params.set('checkout[shipping_address][first_name]', formData.firstName);
            if (formData.lastName) params.set('checkout[shipping_address][last_name]', formData.lastName);
            const qs = params.toString();
            window.location.href = `/checkout${qs ? '?' + qs : ''}`;
        } catch (err) {
            console.error('[BulkOrder] Checkout error:', err);
            alert('Failed to add to cart. Please try again.');
        }
    }

    // ── Build summary HTML ──
    buildSummaryHTML() {
        const d = this.getFormData();
        const name = [d.firstName, d.lastName].filter(Boolean).join(' ');
        return `
            <div class="bulk-order-form__summary-row">
                <span class="bulk-order-form__summary-label">Name</span>
                <span class="bulk-order-form__summary-value">${this.esc(name)}</span>
            </div>
            <div class="bulk-order-form__summary-row">
                <span class="bulk-order-form__summary-label">Email</span>
                <span class="bulk-order-form__summary-value">${this.esc(d.email)}</span>
            </div>
            <div class="bulk-order-form__summary-row">
                <span class="bulk-order-form__summary-label">Magnet Size</span>
                <span class="bulk-order-form__summary-value">${this.esc(d.size)}</span>
            </div>
            <div class="bulk-order-form__summary-row">
                <span class="bulk-order-form__summary-label">Quantity</span>
                <span class="bulk-order-form__summary-value">${this.esc(d.quantity)}</span>
            </div>
            <div class="bulk-order-form__summary-row">
                <span class="bulk-order-form__summary-label">Subtotal</span>
                <span class="bulk-order-form__summary-value bulk-order-form__summary-value--highlight">${this.esc(d.totalPrice)}</span>
            </div>
            <div class="bulk-order-form__summary-row">
                <span class="bulk-order-form__summary-label">Photos Uploaded</span>
                <span class="bulk-order-form__summary-value">${this.files.length} file(s)</span>
            </div>
            ${d.deadline ? `
            <div class="bulk-order-form__summary-row">
                <span class="bulk-order-form__summary-label">Deadline</span>
                <span class="bulk-order-form__summary-value">${this.esc(d.deadline)}</span>
            </div>` : ''}
            ${d.notes ? `
            <div class="bulk-order-form__summary-row">
                <span class="bulk-order-form__summary-label">Notes</span>
                <span class="bulk-order-form__summary-value">${this.esc(d.notes)}</span>
            </div>` : ''}
        `;
    }

    // ── Show confirmation ──
    showConfirmation() {
        const html = this.buildSummaryHTML();
        if (this.isMobile()) {
            if (this.modalSummaryContent) this.modalSummaryContent.innerHTML = html;
            this.openModal();
        } else {
            if (this.summaryContent) this.summaryContent.innerHTML = html;
            this.goToStep(2);
        }
    }

    // ── Submit to Worker ──
    async submitToWorker(photoUrls) {
        const cfg = this.getUploadConfig();
        const formData = this.getFormData();
        const name = [formData.firstName, formData.lastName].filter(Boolean).join(' ');

        const payload = {
            name,
            email: formData.email,
            quantity: formData.quantity,
            size: formData.size,
            sizeRaw: formData.sizeRaw,
            unitPrice: formData.unitPrice,
            totalPrice: formData.totalPrice,
            deadline: formData.deadline,
            notes: formData.notes,
            photoUrls,
        };

        const res = await fetch(`${cfg.url}/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': cfg.apiKey,
            },
            body: JSON.stringify(payload),
        });

        const result = await res.json();
        if (!res.ok || result.error) {
            throw new Error(result.error || 'Submission failed');
        }
        return result;
    }

    // ── Handle form submit ──
    async handleSubmit() {
        if (this.isUploading) return;

        if (this.files.length === 0) { alert('Please upload at least one file.'); return; }

        const formData = this.getFormData();
        if (!formData.firstName || !formData.email) { alert('Please fill in all required fields.'); return; }

        const qty = parseInt(formData.quantity, 10);
        if (!qty || qty < this.minQty) { alert(`Minimum quantity is ${this.minQty}.`); return; }

        if (!formData.variantId) { alert('Please select a magnet size.'); return; }

        this.isUploading = true;
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = 'Uploading photos...';
        this.errorMsg?.classList.remove('active');

        try {
            if (!this.isUploadConfigured()) {
                throw new Error('Upload API not configured. Please contact the store owner.');
            }

            // Step 1: Upload all photos
            const photoUrls = await this.uploadAllFiles();

            // Step 2: Submit form data + URLs to Worker
            this.setProgress(100, 'Submitting order...');
            this.submitBtn.textContent = 'Submitting order...';
            const result = await this.submitToWorker(photoUrls);

            // Store photo data for checkout
            this.uploadedPhotoUrls = photoUrls;
            this.galleryUrl = result.galleryUrl || '';
            this.zipUrl = result.zipUrl || '';

            // Step 3: Show confirmation
            this.hideProgress();
            this.submitBtn.textContent = 'Submitted ✓';
            this.showConfirmation();

        } catch (err) {
            console.error('[BulkOrder] ❌ Error:', err);
            this.hideProgress();
            this.submitBtn.disabled = false;
            this.submitBtn.textContent = 'Submit';
            this.isUploading = false;
            if (this.errorMsg) {
                this.errorMsg.textContent = '❌ ' + err.message;
                this.errorMsg.classList.add('active');
            }
        }
    }
}

customElements.define('bulk-order-form', BulkOrderForm);
